import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { MENU_SECTIONS, isMenuSubGroup, type MenuPath } from "@/lib/menu-paths";
import { settingsLabelForPath } from "@/lib/settings-catalog";
import { ROUTES } from "@/routes/paths";

/**
 * Rasto de navegação do cabeçalho — "Recursos Humanos › Colaboradores".
 *
 * Os rótulos saem do `MENU_SECTIONS`, a mesma fonte que desenha o menu lateral:
 * assim o topo nunca chama a uma página um nome diferente do que o menu lhe
 * chama. O último elemento não é ligação (já estamos lá).
 *
 * Páginas de detalhe (`/dashboard/clients/42`) não têm entrada no menu, por isso
 * casamos pelo caminho mais longo que seja prefixo do actual — dá o rasto da
 * secção mãe, que é o que interessa para situar quem está a ler.
 */

export interface Crumb {
  label: string;
  path?: string;
}

/** Índice caminho → rasto, construído uma vez (o menu é estático). */
const TRAILS: Array<{ path: string; crumbs: Crumb[] }> = MENU_SECTIONS.flatMap((section) =>
  section.items.flatMap((item) => {
    const build = (menu: MenuPath, groupLabel?: string): { path: string; crumbs: Crumb[] } => ({
      path: menu.path,
      crumbs: [
        // Secções de um só item não repetem o nome da secção, e uma secção
        // `standalone` (o topo: Painel, Calendário, Tarefas...) não é pai de
        // nada — o rasto é só o título da página, sem "Dashboard ›".
        ...(section.standalone || section.title === menu.label ? [] : [{ label: section.title }]),
        ...(groupLabel ? [{ label: groupLabel }] : []),
        { label: menu.label, path: menu.path },
      ],
    });

    return isMenuSubGroup(item)
      ? item.items.map((si) => build(si, item.groupLabel))
      : [build(item)];
  })
).sort((a, b) => b.path.length - a.path.length); // mais específico primeiro

/**
 * `/dashboard` é prefixo de toda a app — se casasse por prefixo, uma página que
 * não existe no menu (um 404, por exemplo) herdava o rasto do Painel e o topo
 * mentia sobre onde estamos. Casa só exactamente.
 */
const EXACT_ONLY = new Set(["/dashboard"]);

/**
 * As Definições têm uma só entrada no menu; os destinos lá dentro (Permissões, Dados da empresa…)
 * são cartões da página, não entradas. O rasto sai do catálogo dela — «Definições › Permissões» —
 * e é por isso que a entrada do menu não pode responder por eles: casaria por prefixo e diria só «Definições».
 */
const settingsCrumbs = (pathname: string): Crumb[] => {
  const home = ROUTES.SETTINGS.MAIN;
  if (pathname === home) return [{ label: "Definições" }];
  const label = settingsLabelForPath(pathname);
  return label ? [{ label: "Definições", path: home }, { label }] : [{ label: "Definições", path: home }];
};

/** O rasto de um caminho — vazio se não pertencer a nenhuma entrada do menu. */
export const crumbsForPath = (pathname: string): Crumb[] => {
  const match = TRAILS.find((t) =>
    EXACT_ONLY.has(t.path)
      ? pathname === t.path
      : pathname === t.path || pathname.startsWith(t.path + "/")
  );
  if (match?.path === ROUTES.SETTINGS.MAIN) return settingsCrumbs(pathname);
  return match?.crumbs ?? [];
};

export const BreadcrumbTrail = () => {
  const { pathname } = useLocation();

  const crumbs = useMemo(() => crumbsForPath(pathname), [pathname]);

  if (crumbs.length === 0) return null;

  return (
    <nav aria-label="Navegação estrutural" className="flex min-w-0 items-center gap-1 text-[13px]">
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1;

        return (
          <span key={`${crumb.label}-${i}`} className="flex min-w-0 items-center gap-1">
            {i > 0 && (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" aria-hidden />
            )}
            {isLast ? (
              <span className="truncate font-medium text-foreground" aria-current="page">
                {crumb.label}
              </span>
            ) : crumb.path ? (
              <Link
                to={crumb.path}
                className="hidden truncate text-muted-foreground transition-colors hover:text-foreground sm:inline"
              >
                {crumb.label}
              </Link>
            ) : (
              // Nome de secção/grupo não é navegável (não tem página própria).
              // Some no telemóvel — aí só cabe a página onde estamos.
              <span className="hidden truncate text-muted-foreground sm:inline">{crumb.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
};
